<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useTranslation } from 'i18next-vue';
import { Info } from 'lucide-vue-next';
import axios from '@/plugins/axios';
import { DSButton } from '@/design-system/components';
import { UploadLimitsCard } from '@/design-system/components/molecules';
import { useThemeTokens } from '@/design-system/composables/useTheme';
import { useLanguage } from '@/composables/useLanguage';
import { SUPPORTED_LANGUAGES, LANGUAGES } from '@/constants/languages';

const { t } = useTranslation();
const { getColor } = useThemeTokens();
const { currentLanguage } = useLanguage();

const primaryColor = computed(() => getColor('primary'));

const dialogVisible = ref(false);
const title = ref('');
const message = ref('');
const actionName = ref('');
const cost = ref(null);
const showCost = ref(false);
const confirmationRequired = ref(false);
const confirmationText = ref('');
const confirmationInput = ref('');
const previewImageUrl = ref(null);
const infoMessage = ref(null);
const creditInfo = ref(null);
const contexts = ref([]);
const creditsRemaining = ref(null);
const uploadLimits = ref(null);
const expandedContextId = ref(null);
const contextContentCache = ref({});
const loadingContextId = ref(null);

const _allowCustomizeOption = ref(false);
const _customizeButtonText = ref('');
const _confirmButtonText = ref('');
// Nome próprio para o botão de sair, para o caso em que ele NÃO é "cancelar" e
// sim a segunda saída da escolha (a etapa 3 do wizard: "concluir agora" ou
// "fechar e deixar rodando em segundo plano"). Vazio = o rótulo de sempre.
const _cancelButtonText = ref('');
const _disableConfirmBtn = ref(false);

const _allowLanguageSelection = ref(false);
const selectedLanguage = ref('pt');

// Optional inline checkbox (e.g. "generate translations on save").
const _checkboxLabel = ref('');
const _checkboxValue = ref(false);

const ctrlKeyPressed = ref(false);

let resolvePromise;

const isConfirmDisabled = computed(() => {
    if (_disableConfirmBtn.value) return true;
    return confirmationRequired.value && confirmationInput.value !== confirmationText.value;
});

const mainActionButtonText = computed(() => {
    if (_allowCustomizeOption.value && ctrlKeyPressed.value) {
        return _customizeButtonText.value || t('components.common.dialogs.confirmation.defaultCustomizeButton');
    }
    return _confirmButtonText.value || t('components.common.dialogs.confirmation.defaultConfirmButton');
});

const mainActionButtonVariant = computed(() => {
    return (_allowCustomizeOption.value && ctrlKeyPressed.value) ? 'secondary' : 'primary';
});

const formattedCost = computed(() => {
    if (cost.value === null || cost.value === undefined) return '';
    // SYN-2738 (I4): valor no locale da UI; moeda segue BRL (o custo não traz código).
    return new Intl.NumberFormat(LANGUAGES[currentLanguage.value]?.locale || 'pt-BR', { style: 'currency', currency: 'BRL' }).format(cost.value);
});

const toggleContext = async (id) => {
    if (expandedContextId.value === id) {
        expandedContextId.value = null;
        return;
    }
    expandedContextId.value = id;
    if (!contextContentCache.value[id]) {
        loadingContextId.value = id;
        try {
            const response = await axios.get(`/folders/${id}/briefing`);
            contextContentCache.value[id] = response.data.content || t('components.common.dialogs.confirmation.noContextContent');
        } catch {
            contextContentCache.value[id] = t('components.common.dialogs.confirmation.contextLoadError');
        } finally {
            loadingContextId.value = null;
        }
    }
};

const handleKeyDown = (event) => {
    if (event.key === 'Control' || event.key === 'Meta') {
        ctrlKeyPressed.value = true;
    }
};

const handleKeyUp = (event) => {
    if (event.key === 'Control' || event.key === 'Meta') {
        ctrlKeyPressed.value = false;
    }
};

const show = ({
    dialogTitle,
    dialogMessage,
    actionName: action = null,
    cost: actionCost = null,
    confirmationRequired: reqConfirm = false,
    confirmationText: confirmText = '',
    allowCustomizeOption: allowCustomize = false,
    customizeButtonText: customText = '',
    confirmButtonText: mainConfirmText = '',
    cancelButtonText: cancelText = '',
    previewImageUrl: imageUrl = null,
    disableConfirmBtn: disableBtn = false,
    info: infoMsg = null,
    credit: creditMsg = null,
    contexts: ctxList = [],
    creditsRemaining: creditsData = null,
    allowLanguageSelection: allowLangSel = false,
    uploadLimits: uploadLimitsData = null,
    checkboxLabel: cbLabel = '',
    checkboxDefault: cbDefault = false
}) => {
    title.value = dialogTitle;
    message.value = dialogMessage;
    actionName.value = action;
    cost.value = actionCost;
    showCost.value = actionName.value !== null && cost.value !== null && cost.value > 0;
    confirmationRequired.value = reqConfirm;
    confirmationText.value = confirmText;
    confirmationInput.value = '';
    ctrlKeyPressed.value = false;
    previewImageUrl.value = imageUrl;
    infoMessage.value = infoMsg;
    creditInfo.value = creditMsg;
    contexts.value = ctxList || [];
    creditsRemaining.value = creditsData;
    uploadLimits.value = uploadLimitsData;
    expandedContextId.value = null;
    contextContentCache.value = {};
    loadingContextId.value = null;

    _allowCustomizeOption.value = allowCustomize;
    _customizeButtonText.value = customText;
    _confirmButtonText.value = mainConfirmText;
    _cancelButtonText.value = cancelText;
    _disableConfirmBtn.value = disableBtn;

    _allowLanguageSelection.value = allowLangSel;
    selectedLanguage.value = currentLanguage.value;

    _checkboxLabel.value = cbLabel;
    _checkboxValue.value = cbDefault;

    dialogVisible.value = true;

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return new Promise((resolve) => {
        resolvePromise = resolve;
    });
};

const cleanupAndClose = () => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);
    
    ctrlKeyPressed.value = false;
    previewImageUrl.value = null;
    uploadLimits.value = null;
    dialogVisible.value = false;
}

const handleMainActionClick = () => {
    if (isConfirmDisabled.value) {
        return;
    }
    if (resolvePromise) {
        resolvePromise({
            confirmed: true,
            customize: _allowCustomizeOption.value && ctrlKeyPressed.value,
            language: _allowLanguageSelection.value ? selectedLanguage.value : null,
            checkboxChecked: _checkboxLabel.value ? _checkboxValue.value : null,
            dismissed: false,
        });
    }
    cleanupAndClose();
};

// ESCOLHER A SEGUNDA OPÇÃO E DISPENSAR A CAIXA NÃO SÃO O MESMO GESTO (16/09).
//
// Enquanto o botão de saída era sempre "Cancelar", tanto fazia: as duas coisas
// queriam dizer "não faça nada". Com `cancelButtonText` o botão ganhou nome
// próprio e virou a SEGUNDA SAÍDA de uma escolha (na etapa 3 do wizard ele é
// "fechar e deixar rodando em segundo plano"), e aí o Esc passou a executar uma
// ação que ninguém pediu. Dispensar um diálogo não pode valer como escolha.
//
// O campo `dismissed` é ADITIVO de propósito: quem só lê `confirmed` continua
// vendo exatamente o que via antes (Esc e o botão de saída seguem `false`), e só
// quem precisa separar os três desfechos lê o campo novo.
const resolveWith = (result) => {
    if (resolvePromise) resolvePromise(result);
    cleanupAndClose();
};

// O botão de saída: é uma ESCOLHA, com a intenção declarada pelo clique.
const cancel = () => resolveWith({ confirmed: false, customize: false, language: null, dismissed: false });

// Esc: a caixa some e nada acontece. Nenhum dos dois ramos da escolha roda.
const dismiss = () => resolveWith({ confirmed: false, customize: false, language: null, dismissed: true });

watch(dialogVisible, (newValue) => {
    if (!newValue) {
        // Failsafe cleanup if dialog is closed unexpectedly
        cleanupAndClose();
    }
});

onUnmounted(() => {
    cleanupAndClose();
});


defineExpose({
    show: show
})
</script>

<template>
    <v-dialog v-model="dialogVisible" max-width="500px" persistent @keydown.esc="dismiss">
        <v-card class="confirm-card">
            <v-card-title class="confirm-title mp-sensitive">{{ title }}</v-card-title>
            <v-card-text class="confirm-body">
                <div v-if="message" class="confirm-message mp-sensitive">{{ message }}</div>

                <!-- Optional inline checkbox (e.g. "generate translations on save") -->
                <div v-if="_checkboxLabel" class="confirm-checkbox">
                    <v-checkbox
                        v-model="_checkboxValue"
                        :label="_checkboxLabel"
                        color="primary"
                        density="compact"
                        hide-details
                    ></v-checkbox>
                </div>

                <!-- Upload limits / violations -->
                <UploadLimitsCard
                    v-if="uploadLimits"
                    :media-type="uploadLimits.mediaType"
                    :violations="uploadLimits.violations || []"
                    :collapsible="!_disableConfirmBtn"
                    class="confirm-upload-limits"
                />

                <!-- Contexts applied -->
                <div v-if="contexts.length > 0 && !_disableConfirmBtn" class="confirm-contexts">
                    <span class="confirm-contexts-label">{{ t('components.common.dialogs.confirmation.contextsLabel') }}</span>
                    <div class="confirm-contexts-chips mp-sensitive">
                        <button
                            v-for="ctx in contexts"
                            :key="ctx.id"
                            type="button"
                            class="context-chip"
                            :class="{ 'context-chip--expanded': expandedContextId === ctx.id }"
                            @click="toggleContext(ctx.id)"
                        >
                            {{ ctx.briefing_name || ctx.name }}
                        </button>
                    </div>
                    <div v-if="expandedContextId" class="context-preview mp-sensitive">
                        <div v-if="loadingContextId === expandedContextId" class="d-flex align-center justify-center pa-2">
                            <v-progress-circular indeterminate size="18" width="2" :color="primaryColor" />
                        </div>
                        <p v-else-if="contextContentCache[expandedContextId]" class="context-preview-text">
                            {{ contextContentCache[expandedContextId] }}
                        </p>
                        <p v-else class="context-preview-text text-grey">{{ t('components.common.dialogs.confirmation.noContextContent') }}</p>
                    </div>
                </div>

                <!-- Image preview -->
                <div v-if="previewImageUrl" class="confirm-preview">
                    <img :src="previewImageUrl" :alt="t('components.common.dialogs.confirmation.previewAlt')" class="confirm-preview-img" />
                </div>

                <!-- Language selection (optional accordion) -->
                <div v-if="_allowLanguageSelection && !_disableConfirmBtn" class="confirm-language">
                    <v-expansion-panels variant="accordion" flat>
                        <v-expansion-panel>
                            <v-expansion-panel-title class="language-panel-title">
                                <div class="d-flex align-center flex-grow-1">
                                    <v-icon size="20" class="mr-2">mdi-translate</v-icon>
                                    <span class="text-body-2">{{ t('components.common.dialogs.confirmation.language.title') }}</span>
                                </div>
                                <template v-slot:actions="{ expanded }">
                                    <img
                                        v-if="!expanded"
                                        :src="SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.flagPath"
                                        :alt="selectedLanguage"
                                        class="flag-icon-sm mr-2"
                                    />
                                    <v-icon size="small">{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                </template>
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <p class="text-caption text-grey mb-2">
                                    {{ t('components.common.dialogs.confirmation.language.description') }}
                                </p>
                                <v-radio-group v-model="selectedLanguage" density="compact" hide-details class="language-radio-group">
                                    <v-radio
                                        v-for="lang in SUPPORTED_LANGUAGES"
                                        :key="lang.code"
                                        :value="lang.code"
                                    >
                                        <template v-slot:label>
                                            <div class="d-flex align-center">
                                                <img :src="lang.flagPath" :alt="lang.flagAlt" class="flag-icon-sm mr-2" />
                                                <span class="text-body-2">{{ t(lang.translationKey) }}</span>
                                            </div>
                                        </template>
                                    </v-radio>
                                </v-radio-group>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </div>

                <div v-if="confirmationRequired && !_disableConfirmBtn" class="mb-4">
                    <!-- SYN-2403: o label interpola `confirmationText` (razão social/e-mail); a classe
                         vai no campo porque o Vuetify a repassa pra raiz `.v-input`, cobrindo o label. -->
                    <v-text-field
                        v-model="confirmationInput"
                        class="mp-sensitive"
                        :label="t('components.common.dialogs.confirmation.confirmationInputLabel', { text: confirmationText })"
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                        :error="confirmationInput && confirmationInput !== confirmationText"
                        autocomplete="off"
                    ></v-text-field>
                </div>

                <div v-if="showCost && !_disableConfirmBtn" class="cost-section">
                     <div class="d-flex justify-space-between align-center">
                        <span class="text-subtitle-2">{{ t('components.common.dialogs.confirmation.action') }}</span>
                        <span class="text-body-2">{{ actionName }}</span>
                     </div>
                    <v-divider class="my-2"></v-divider>
                     <div class="d-flex justify-space-between align-center">
                        <span class="text-subtitle-2">{{ t('components.common.dialogs.confirmation.additionalCost') }}</span>
                        <span class="text-body-2 font-weight-bold text-error">
                            <v-icon size="small" class="mr-1" color="error">mdi-cash-multiple</v-icon>
                            {{ formattedCost }}
                        </span>
                     </div>
                </div>

                <div v-if="infoMessage" class="info-banner">
                    <Info :size="18" class="info-icon" />
                    <span class="info-text">{{ infoMessage }}</span>
                </div>
            </v-card-text>
            <v-card-actions class="confirm-actions">
                <v-spacer></v-spacer>
                <DSButton variant="ghost" @click="cancel" class="confirm-cancel-btn">
                    {{ _cancelButtonText || (_disableConfirmBtn ? t('components.common.dialogs.confirmation.buttons.close') : t('components.common.dialogs.confirmation.buttons.cancel')) }}
                </DSButton>
                <DSButton
                    :variant="mainActionButtonVariant"
                    @click="handleMainActionClick"
                    :disabled="isConfirmDisabled"
                    class="confirm-action-btn"
                >
                    {{ mainActionButtonText }}
                </DSButton>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* Card */
.confirm-card {
    border-radius: 16px !important;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
    overflow: hidden;
}
.confirm-title {
    font-family: "Noto Sans", sans-serif;
    font-size: 18px !important;
    font-weight: 600 !important;
    color: var(--color-text-primary);
    padding: 24px 24px 0 !important;
    line-height: 1.3;
}
.confirm-body {
    padding: 16px 24px 8px !important;
}
.confirm-message {
    font-size: 14px;
    color: var(--color-text-secondary);
    line-height: 1.6;
    white-space: pre-wrap;
    margin-bottom: 16px;
}

.confirm-upload-limits {
    margin-bottom: 16px;
}

/* Contexts */
.confirm-contexts {
    margin-bottom: 16px;
}
.confirm-contexts-label {
    display: block;
    font-size: var(--font-size-label-small, 12px);
    font-weight: 500;
    color: var(--color-text-secondary);
    margin-bottom: 8px;
    letter-spacing: 0.02em;
}
.confirm-contexts-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.context-chip {
    display: inline-flex;
    align-items: center;
    padding: 5px 12px;
    background: var(--color-background-light);
    border: 1px solid var(--color-border-subtle);
    border-radius: 6px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--color-text-primary);
    cursor: pointer;
    transition: all 0.12s ease;
}
.context-chip:hover {
    background: var(--color-tertiary);
    border-color: var(--color-border-default);
}
.context-chip--expanded {
    background: rgba(229, 57, 53, 0.08);
    border-color: rgba(229, 57, 53, 0.25);
    color: var(--color-error-dark);
}
.context-preview {
    margin-top: 8px;
    padding: 10px 12px;
    background: var(--color-background-light);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    max-height: 120px;
    overflow-y: auto;
}
.context-preview-text {
    font-size: 12.5px;
    color: var(--color-text-secondary);
    line-height: 1.5;
    margin: 0;
    white-space: pre-wrap;
}

/* Preview image */
.confirm-preview {
    margin-bottom: 16px;
    border-radius: 12px;
    overflow: hidden;
    background: var(--color-background-light);
    border: 1px solid var(--color-border-subtle);
}
.confirm-preview-img {
    width: 100%;
    max-height: 220px;
    object-fit: contain;
    display: block;
}

/* Cost */
.cost-section {
    background-color: var(--color-warning-light);
    border: 1px solid var(--color-warning-light);
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 16px;
}
.cost-section .v-icon {
    vertical-align: text-bottom;
    margin-top: -2px;
}

/* Info banner */
.info-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    background: var(--color-error-light);
    border-radius: 10px;
    border: 1px solid var(--color-error-light);
    margin-bottom: 8px;
}
.info-icon {
    flex-shrink: 0;
    color: var(--color-error);
    margin-top: 1px;
}
.info-text {
    font-size: 13px;
    color: var(--color-error-dark);
    line-height: 1.45;
}

/* Actions */
.confirm-actions {
    padding: 16px 24px 20px !important;
    gap: 8px;
    border-top: 1px solid var(--color-border-subtle);
    /* Rótulo longo não estoura o cartão (16/09): o v-card-actions herda nowrap
       dos botões do Vuetify, e o "Fechar e deixar rodando em segundo plano" da
       etapa de prioridades saía cortado pela borda esquerda. A fileira quebra
       linha quando falta espaço, e cada botão pode encolher e quebrar o próprio
       texto em vez de vazar. */
    flex-wrap: wrap;
}
.confirm-actions :deep(.confirm-cancel-btn),
.confirm-actions :deep(.confirm-action-btn) {
    min-width: 0;
    max-width: 100%;
    white-space: normal;
    height: auto;
    text-align: center;
}
.confirm-cancel-btn {
    --ds-button-color: var(--color-text-secondary);
}
.confirm-action-btn {
    /* DSButton handles styling */
}

:deep(.v-text-field--error .v-field__outline) {
    color: orange !important;
}
:deep(.text-error-message) {
    color: var(--color-error);
    font-weight: 500;
    background-color: var(--color-error-light);
    padding: 8px;
    border-radius: 4px;
    border: 1px solid var(--color-error-light);
    margin-top: 8px;
    display: block;
}

/* Language selection accordion */
.confirm-language {
    margin-bottom: 16px;
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    overflow: hidden;
    background: var(--color-background-light);
}
.confirm-language :deep(.v-expansion-panel) {
    background: transparent !important;
}
.confirm-language :deep(.v-expansion-panel-title) {
    min-height: 44px !important;
    padding: 8px 14px !important;
}
.confirm-language :deep(.v-expansion-panel-text__wrapper) {
    padding: 8px 14px 12px !important;
}
.flag-icon-sm {
    width: 20px;
    height: 14px;
    object-fit: cover;
    border-radius: 2px;
    vertical-align: middle;
}
.language-radio-group :deep(.v-selection-control) {
    min-height: 32px;
}
</style>
